// A gallery of pictures, as the CMS image field with several images keeps one: images dropped onto it (several
// at once) or picked are uploaded one after the other and added; the thumbnails are dragged to reorder them and
// each can be removed. `value`: the pictures (their stored paths, in order); `onChange(list)`; `upload(file)` →
// { url } (the stored path); `src(path)`: a picture's url to show; `max`: how many it holds.
import React, { useRef, useState } from "react"
import { ArrowUpTrayIcon, Bars2Icon, PhotoIcon, XMarkIcon } from "@heroicons/react/24/outline"
import Spinner from "aldoo-ra/Spinner"

const IMAGE = /^image\//

export default function GalleryField({ value = [], onChange, upload, src = (path) => path, max = 8, disabled = false, t = (text) => text, "data-testid": testId = "gallery-field" }) {
  const list = Array.isArray(value) ? value : []
  const input = useRef(null)
  const [dragged, setDragged] = useState(null)
  const [over, setOver] = useState(false)
  const [busy, setBusy] = useState(0)
  const [error, setError] = useState("")
  // the latest list while uploads are in flight (each adds to what the one before left)
  const latest = useRef(list)
  latest.current = list
  const room = Math.max(0, max - list.length)

  const addFiles = async (files) => {
    const images = [...(files || [])].filter((file) => IMAGE.test(file.type))
    if (!images.length) return setError(t`Drop image files (JPG, PNG, WebP)`)
    const take = images.slice(0, room)
    setError(images.length > room ? t("Up to %s pictures: %s were left out", max, images.length - room) : "")
    for (const file of take) {
      setBusy((n) => n + 1)
      try {
        const res = await upload(file)
        if (res?.url) {
          latest.current = [...latest.current, res.url].slice(0, max)
          onChange?.(latest.current)
        } else setError(res?.message || res?.error || t`A picture could not be uploaded`)
      } catch (failure) {
        setError(failure?.message || t`A picture could not be uploaded`)
      } finally {
        setBusy((n) => n - 1)
      }
    }
  }

  // a thumbnail dragged over another takes its place
  const dragOver = (event, index) => {
    event.preventDefault()
    if (dragged === null || dragged === index) return
    const next = [...list]
    const [moved] = next.splice(dragged, 1)
    next.splice(index, 0, moved)
    onChange?.(next)
    setDragged(index)
  }
  // files from the computer, dropped anywhere on the field (not a thumbnail being reordered)
  const filesDragged = (event) => dragged === null && [...(event.dataTransfer?.types || [])].includes("Files")

  return (
    <div
      className={`flex flex-col gap-2 rounded-lg border-2 border-dashed p-3 transition-colors ${over ? "border-admin_primary bg-admin_primary/5" : "border-admin_bordered"}`}
      onDragOver={(event) => {
        if (!filesDragged(event) || disabled) return
        event.preventDefault()
        setOver(true)
      }}
      onDragLeave={(event) => !event.currentTarget.contains(event.relatedTarget) && setOver(false)}
      onDrop={(event) => {
        if (!filesDragged(event) || disabled) return
        event.preventDefault()
        setOver(false)
        addFiles(event.dataTransfer.files)
      }}
      data-testid={testId}
    >
      <div className="grid gap-2 [grid-template-columns:repeat(auto-fill,minmax(96px,1fr))]">
        {list.map((path, index) => (
          <div
            key={path}
            draggable={!disabled && list.length > 1}
            onDragStart={() => setDragged(index)}
            onDragOver={(event) => dragged !== null && dragOver(event, index)}
            onDragEnd={() => setDragged(null)}
            className={`group relative aspect-square rounded-md overflow-hidden border bg-admin_neutral_1 ${dragged === index ? "opacity-50 border-admin_primary" : "border-admin_bordered"} ${!disabled && list.length > 1 ? "cursor-grab active:cursor-grabbing" : ""}`}
            data-testid="gallery-item"
          >
            <img src={src(path)} alt="" className="w-full h-full object-cover pointer-events-none" />
            {!disabled && list.length > 1 && (
              <span className="absolute top-1 left-1 rounded bg-white/85 p-0.5 text-admin_text_secondary" title={t`Drag to reorder`}>
                <Bars2Icon className="w-3.5 h-3.5" />
              </span>
            )}
            <span className="absolute bottom-1 left-1 rounded bg-black/55 px-1 text-[10px] font-semibold text-white">{index + 1}</span>
            {!disabled && (
              <button
                type="button"
                onClick={() => onChange?.(list.filter((_, at) => at !== index))}
                className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/60 text-white inline-flex items-center justify-center opacity-90 hover:opacity-100"
                aria-label={t`Remove`}
                title={t`Remove`}
                data-testid="gallery-remove"
              >
                <XMarkIcon className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        ))}
        {Array.from({ length: busy }).map((_, index) => (
          <div key={`busy-${index}`} className="aspect-square rounded-md border border-admin_bordered flex items-center justify-center">
            <Spinner variant="admin_primary" size="sm" />
          </div>
        ))}
        {!disabled && room > busy && (
          <button
            type="button"
            onClick={() => input.current?.click()}
            className="aspect-square rounded-md border border-dashed border-admin_bordered flex flex-col items-center justify-center gap-1 text-xs text-admin_text_secondary hover:border-admin_primary hover:text-admin_primary"
            data-testid="gallery-add"
          >
            {list.length ? <ArrowUpTrayIcon className="w-5 h-5" /> : <PhotoIcon className="w-6 h-6" />}
            {t`Add`}
          </button>
        )}
      </div>
      <div className="text-xs text-admin_text_secondary">
        {t("Drop pictures here or add them, up to %s; drag to reorder", max)}
      </div>
      {error && <div className="text-xs text-admin_error" role="alert">{error}</div>}
      <input
        ref={input}
        type="file"
        accept="image/*"
        multiple
        hidden
        onChange={(event) => {
          addFiles(event.target.files)
          event.target.value = ""
        }}
      />
    </div>
  )
}
